import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import CustomLoading from '../../../util_components/custom_loading';
import CustomModal from '../../../util_components/custom_modal';
import { post_api, get_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { reschedule_log_view } from '../../../util_components/care_request/who_asked_rules';
import {
  format_sub_status,
  ist_date,
  ist_datetime,
  ist_time,
  visible_status_history
} from './session_logs_rules';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { session_csv_name, with_session_uuid } from './session_care_csv';
import {
  REPLACEMENT_LOG_CSV_COLUMNS,
  RESCHEDULE_LOG_CSV_COLUMNS,
  STATUS_HISTORY_CSV_COLUMNS
} from './session_logs_csv';
import './session_csv.css';

// the resolved / Care answer, with the Disagreement or Resolved marker (detail on hover too).
function WhoAskedCell({ log }) {
  const who = reschedule_log_view(log);
  return (
    <td>
      {who.shown}
      {who.marker ? (
        <div>
          <span className={`who-asked-marker who-asked-marker-${who.marker}`} title={who.detail}>
            {who.marker_text}
          </span>
          <div className="change-history-muted">{who.detail}</div>
        </div>
      ) : null}
    </td>
  );
}

WhoAskedCell.propTypes = { log: PropTypes.shape({ who_asked_state: PropTypes.string }).isRequired };

class SessionLogsViewer extends React.Component {
  state = {
    loading: true,
    replacementLogs: [],
    rescheduleLogs: [],
    statusHistory: [],
    statusHistoryError: false
  };

  componentDidMount() {
    const { sessionUuid, type } = this.props;
    if (type === 'replacement') this.fetchReplacementLogs(sessionUuid);
    if (type === 'reschedule') this.fetchRescheduleLogs(sessionUuid);
    if (type === 'status') this.fetchStatusHistory(sessionUuid);
  }

  fetchReplacementLogs = (session_uuid) => {
    const url = get_api_url('get_request_replacement_logs');
    const payload = { session_uuid };

    post_api(url, payload, true)
      .then((res) => {
        const appLogs = res?.data?.request_replacement || [];
        const manualLogs = res?.data?.session_change_teacher_history || [];

        const unified = [
          ...appLogs.flatMap((item) => {
            const logs = item.request_replacement_logs;
            if (logs && logs.length > 0) {
              return logs.map((log) => ({
                mode: 'Teacher Initiated',
                status: log.replacement_request_status || '--',
                teacherName: log.accepted_by_teacher?.name || '--',
                fromTeacherName: item.teacher_name || '--',
                fromTeacherUuid: item.teacher_uuid || '--',
                toTeacherName:
                  log.accepted_by_teacher?.name || item.teacher_transferred_to_name || '--',
                toTeacherUuid:
                  log.accepted_by_teacher?.uuid || item.teacher_transferred_to_uuid || '--',
                time: log.accepted_time || item.created_date,
                doneBy: '--',
                notes:
                  [
                    log.reason_for_replacement,
                    log.details_for_replacement || item.details_for_replacement
                  ]
                    .filter(Boolean)
                    .join(', ') || '--'
              }));
            }
            return [
              {
                mode: 'Teacher Initiated',
                status: item?.status || '--',
                teacherName: item?.teacher_name || '--',
                fromTeacherName: item?.teacher_name || '--',
                fromTeacherUuid: item?.teacher_uuid || '--',
                toTeacherName: item?.teacher_transferred_to_name || '--',
                toTeacherUuid: item?.teacher_transferred_to_uuid || '--',
                time: item?.created_date,
                doneBy: '--',
                notes:
                  [item?.reason_for_replacement, item?.details_for_replacement]
                    .filter(Boolean)
                    .join(', ') || '--'
              }
            ];
          }),
          ...manualLogs.map((log) => ({
            mode: 'Care Team Initiated',
            status: 'Accepted',
            fromTeacherName: log?.original_teacher?.name || '--',
            fromTeacherUuid: log?.original_teacher?.uuid || '--',
            toTeacherName: log?.new_teacher?.name || '--',
            toTeacherUuid: log?.new_teacher?.uuid || '--',
            time: log?.replacement_date,
            doneBy: log?.cms_user?.name || '--',
            notes:
              [log?.reason_for_replacement, log?.details_for_replacement]
                .filter(Boolean)
                .join(', ') || '--'
          }))
        ];

        unified.sort((a, b) => new Date(a.time) - new Date(b.time));

        this.setState({ replacementLogs: unified, loading: false });
      })
      .catch(() => this.setState({ loading: false }));
  };

  fetchStatusHistory = (session_uuid) => {
    const url = get_api_url('get_session_status_history');
    const payload = { session_uuid };

    post_api(url, payload, true)
      .then((res) => {
        this.setState({
          statusHistory: visible_status_history(res?.data?.session_status_history),
          loading: false
        });
      })
      // distinct error state so a failed fetch isn't shown as "no history"
      .catch(() => this.setState({ loading: false, statusHistoryError: true }));
  };

  fetchRescheduleLogs = (session_uuid) => {
    const url = get_api_url('get_session_time_change_history');
    const payload = { session_uuid };

    post_api(url, payload, true)
      .then((res) => {
        this.setState({
          rescheduleLogs: res?.data?.session_change_time_history?.returned_obj_arr || [],
          loading: false
        });
      })
      .catch(() => this.setState({ loading: false }));
  };

  render() {
    const { show, close, type, iana_timezone, sessionUuid } = this.props;
    // Heading with the table's Download CSV on the right.
    const header = (title, columns, rows, table) => (
      <div className="session-csv-hd">
        <h3>{title}</h3>
        <DownloadCsvButton
          columns={with_session_uuid(columns, sessionUuid)}
          rows={rows}
          file_name={() => session_csv_name(sessionUuid, table)}
        />
      </div>
    );
    const { loading, replacementLogs, rescheduleLogs, statusHistory, statusHistoryError } =
      this.state;
    const teacher_link = (uuid, name) =>
      uuid && uuid !== '--' ? (
        <a
          className="link-no-dec cursor-pointer text-brand-secondary"
          target="_blank"
          href={`/teacherprofile?id=${uuid}`}
          rel="noreferrer noopener"
        >
          {name}
        </a>
      ) : (
        name
      );

    return (
      <CustomModal show={show} close={close} variant="table">
        {loading ? (
          <CustomLoading />
        ) : type === 'replacement' ? (
          replacementLogs.length > 0 ? (
            <>
              {header(
                'Replacement Logs',
                REPLACEMENT_LOG_CSV_COLUMNS,
                replacementLogs,
                'replacement-logs'
              )}
              <div className="myt-log-table-scroll">
                <table className="myt-log-table">
                  <thead>
                    <tr>
                      <th>Mode</th>
                      <th>Status</th>
                      <th>From Teacher</th>
                      <th>To Teacher</th>
                      <th>Time & Date</th>
                      <th>Done By</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {replacementLogs.map((log, idx) => (
                      <tr key={idx}>
                        <td>{log?.mode}</td>
                        <td>{log?.status}</td>
                        <td>{teacher_link(log?.fromTeacherUuid, log?.fromTeacherName)}</td>
                        <td>{teacher_link(log?.toTeacherUuid, log?.toTeacherName)}</td>
                        <td className="myt-num">
                          {moment(log?.time).tz(iana_timezone).format('YYYY-MM-DD hh:mm A z')}
                        </td>
                        <td>{log?.doneBy}</td>
                        <td>{log?.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="myt-empty">No replacement logs found.</div>
          )
        ) : type === 'status' ? (
          statusHistoryError ? (
            <div className="myt-empty">Failed to load status history</div>
          ) : statusHistory.length > 0 ? (
            <>
              {header(
                'Status History',
                STATUS_HISTORY_CSV_COLUMNS,
                statusHistory,
                'status-history'
              )}
              <div className="myt-log-table-scroll">
                <table className="myt-log-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Sub status</th>
                      <th>Changed by</th>
                      <th>Date/time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statusHistory.map((log, idx) => {
                      const changed_by = log?.changed_by || '--';
                      const actor_name = log?.actor_name || changed_by;
                      return (
                        <tr key={idx}>
                          <td>{log?.status || '--'}</td>
                          <td>{format_sub_status(log?.sub_status)}</td>
                          <td>
                            <div>{changed_by}</div>
                            {/* skip the second line when there's no name beyond the role */}
                            {actor_name !== changed_by ? (
                              <div className="myt-cell-sub">{actor_name}</div>
                            ) : null}
                          </td>
                          <td className="myt-num">{ist_datetime(log?.created_date)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="myt-empty">No status changes recorded</div>
          )
        ) : rescheduleLogs.length > 0 ? (
          <>
            {header(
              'Reschedule Logs',
              RESCHEDULE_LOG_CSV_COLUMNS,
              rescheduleLogs,
              'reschedule-logs'
            )}
            <div className="myt-log-table-scroll">
              <table className="myt-log-table">
                <thead>
                  <tr>
                    <th>Original Time</th>
                    <th>New Time</th>
                    <th>Action Time</th>
                    <th>Changed For</th>
                    <th>Changed By</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {rescheduleLogs.map((log, idx) => (
                    <tr key={idx}>
                      {[
                        log?.original_session_time,
                        log?.current_session_time,
                        log?.created_date
                      ].map((at, i) => (
                        <td className="myt-num" key={i}>
                          <div>{ist_date(at)}</div>
                          <div className="myt-cell-sub">{ist_time(at)}</div>
                        </td>
                      ))}
                      <WhoAskedCell log={log} />
                      <td>
                        <div>{log?.requester_type}</div>
                        <div className="myt-cell-sub">
                          {log?.requester_type === 'TEACHER'
                            ? teacher_link(log?.requester_uuid, log?.requester_name || '--')
                            : log?.requester_name || '--'}
                        </div>
                      </td>
                      <td>{log?.reason_for_reschedule || '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="myt-empty">No reschedule logs found.</div>
        )}
      </CustomModal>
    );
  }
}

export default SessionLogsViewer;
